import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Button, CircularProgress, Alert,
  Pagination, Dialog, DialogTitle, DialogContent, Tabs, Tab
} from '@mui/material';

export default function PromotionsPage() {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [positionType, setPositionType] = useState('0');
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchPromotions() {
      setLoading(true);
      setErrMsg('');
      try {
        const token = localStorage.getItem('token');
        const url = `https://iconmarketing.shop/api/designation/promotionhistory/admin?page=${page}&type=${positionType === '0' ? 'undefined' : positionType}`;
        const options = { headers: { Authorization: `Bearer ${token}` } };
        const res = await fetch(url, options);
        const data = await res.json();
        if (res.ok && data.products) {
          setPromotions(data.products);
          setTotalPages(data.pages || 1);
          setTotalResults(data.countProducts || data.products.length);
        } else {
          setErrMsg(data.message || 'Failed to fetch promotions');
        }
      } catch (err) {
        setErrMsg('Network error');
      } finally {
        setLoading(false);
      }
    }
    fetchPromotions();
  }, [page, positionType]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  const handleRowClick = (promotion) => {
    setSelectedPromotion(promotion);
    setTabIndex(0);
  };
  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #ccc', pb: 1, marginBottom: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Promotions</Typography>
          <Button variant="text" color="primary" onClick={() => navigate('/users')}>Users</Button>
        </Box>
        <Typography variant="h4" color="primary">IconMarketing Promotions</Typography>
        <Button variant="outlined" color="error" onClick={handleLogout}>Logout</Button>
      </Box>
      {/* Position Filter Dropdown */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <label htmlFor="positionType" style={{ fontWeight: 600 }}>Position:</label>
        <select
          id="positionType"
          className="form-control"
          style={{ minWidth: 180, padding: '6px 12px', borderRadius: 4, border: '1px solid #ccc' }}
          value={positionType}
          onChange={e => { setPositionType(e.target.value); setPage(1); }}
        >
          <option value="0">All Position</option>
          <option value="1">Development Officer</option>
          <option value="2">Assistant Manager</option>
          <option value="3">Deputy Manager</option>
          <option value="4">Manager</option>
          <option value="5">General Manager</option>
          <option value="6">Ambassador</option>
          <option value="7">Global Ambassador</option>
        </select>
      </Box>
      {/* Results, Loading, Error */}
      {loading || errMsg ? (
        <Box>
          {loading && (
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <CircularProgress />
            </Box>
          )}
          {errMsg && !loading && (
            <Alert severity="error">{errMsg}</Alert>
          )}
        </Box>
      ) : (
        <Box>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, textAlign: 'right', color: 'text.secondary' }}>
            Showing {promotions.length} promotions on this page. Total found: {totalResults}
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Position Name</TableCell>
                  <TableCell>Position Serial</TableCell>
                  <TableCell>Carry Left</TableCell>
                  <TableCell>Carry Right</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reward</TableCell>
                  <TableCell>Is Running</TableCell>
                  <TableCell>Updated By</TableCell>
                  <TableCell>Updated At</TableCell>
                  <TableCell>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {promotions.map(promotion => (
                  <TableRow key={promotion._id} hover style={{ cursor: 'pointer' }} onClick={() => handleRowClick(promotion)}>
                    <TableCell>{promotion.username}</TableCell>
                    <TableCell>{promotion.position_name}</TableCell>
                    <TableCell>{promotion.position_serial}</TableCell>
                    <TableCell>{promotion.carry_left}</TableCell>
                    <TableCell>{promotion.carry_right}</TableCell>
                    <TableCell>{promotion.status}</TableCell>
                    <TableCell>{promotion.reward}</TableCell>
                    <TableCell>{promotion.isRunning ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{promotion.updated_by}</TableCell>
                    <TableCell>{promotion.updated_at ? new Date(promotion.updated_at).toLocaleString() : '-'}</TableCell>
                    <TableCell>{promotion.createdAt ? new Date(promotion.createdAt).toLocaleString() : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
            />
          </Box>
          {/* Promotion Details Modal */}
          <Dialog open={!!selectedPromotion} onClose={() => setSelectedPromotion(null)} maxWidth="md" fullWidth>
            <DialogTitle>Promotion Details</DialogTitle>
            <DialogContent>
              <Box>
                <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ mb: 2 }}>
                  <Tab label="Main" />
                  <Tab label="Status & Reward" />
                  <Tab label="Dates" />
                  <Tab label="Other" />
                </Tabs>
                {selectedPromotion && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {tabIndex === 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2">Username: {selectedPromotion.username}</Typography>
                        <Typography variant="body2">Position Name: {selectedPromotion.position_name}</Typography>
                        <Typography variant="body2">Position Serial: {selectedPromotion.position_serial}</Typography>
                        <Typography variant="body2">Carry Left: {selectedPromotion.carry_left}</Typography>
                        <Typography variant="body2">Carry Right: {selectedPromotion.carry_right}</Typography>
                      </Box>
                    )}
                    {tabIndex === 1 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2">Status: {selectedPromotion.status}</Typography>
                        <Typography variant="body2">Reward: {selectedPromotion.reward}</Typography>
                        <Typography variant="body2">Is Running: {selectedPromotion.isRunning ? 'Yes' : 'No'}</Typography>
                        <Typography variant="body2">Updated By: {selectedPromotion.updated_by}</Typography>
                      </Box>
                    )}
                    {tabIndex === 2 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2">Created At: {selectedPromotion.createdAt ? new Date(selectedPromotion.createdAt).toLocaleString() : '-'}</Typography>
                        <Typography variant="body2">Updated At: {selectedPromotion.updated_at ? new Date(selectedPromotion.updated_at).toLocaleString() : '-'}</Typography>
                        <Typography variant="body2">UpdatedAt: {selectedPromotion.updatedAt ? new Date(selectedPromotion.updatedAt).toLocaleString() : '-'}</Typography>
                      </Box>
                    )}
                    {tabIndex === 3 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {Object.entries(selectedPromotion).map(([key, value]) => (
                          [
                            '_id','username','position_name','position_serial','carry_left','carry_right','status','reward','isRunning','updated_by','updated_at','createdAt','updatedAt','__v'
                          ].includes(key) ? null : (
                            <Box key={key} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120 }}>{key}:</Typography>
                              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{String(value)}</Typography>
                            </Box>
                          )
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </DialogContent>
          </Dialog>
        </Box>
      )}
    </Container>
  );
}
